import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  uploadAttendance,
  downloadAttendanceTemplate,
  downloadAttendanceExport,
  downloadMonthlyAttendanceExport,
  getMonthlyAttendance,
  getEmployeeAttendanceHistory,
  overrideAttendance,
  getCycleAttendance,
  getEmployeeCycleAttendance,
  getEmployeeYearSummary,
  downloadLopTemplate,
} from '../api';

export const useMonthlyAttendance = (params) => {
  return useQuery({
    queryKey: ['monthlyAttendance', params],
    queryFn: () => getMonthlyAttendance(params),
    keepPreviousData: true,
  });
};

export const useEmployeeAttendanceHistory = (employeeNo, params, enabled = true) => {
  return useQuery({
    queryKey: ['employeeAttendanceHistory', employeeNo, params],
    queryFn: () => getEmployeeAttendanceHistory(employeeNo, params),
    enabled: Boolean(employeeNo) && enabled,
    keepPreviousData: true,
  });
};

// ----- 25→25 cycle dashboards -----

export const useCycleAttendance = (params) => {
  return useQuery({
    queryKey: ['cycleAttendance', params],
    queryFn: () => getCycleAttendance(params),
    enabled: Boolean(params?.cycle),
    keepPreviousData: true,
  });
};

export const useEmployeeCycleAttendance = (employeeNo, params) => {
  return useQuery({
    queryKey: ['employeeCycleAttendance', employeeNo, params],
    queryFn: () => getEmployeeCycleAttendance(employeeNo, params),
    enabled: Boolean(employeeNo) && Boolean(params?.cycle),
    keepPreviousData: true,
  });
};

export const useEmployeeYearSummary = (employeeNo, params) => {
  return useQuery({
    queryKey: ['employeeYearSummary', employeeNo, params],
    queryFn: () => getEmployeeYearSummary(employeeNo, params),
    enabled: Boolean(employeeNo),
    keepPreviousData: true,
  });
};

export const useDownloadLopTemplate = () => {
  return useMutation({
    mutationFn: (params) => downloadLopTemplate(params),
    onSuccess: () => {
      toast.success('LOP template downloaded.', { autoClose: 3000, position: 'top-right' });
    },
    onError: () => {
      toast.error('Failed to download LOP template.', { autoClose: 4000, position: 'top-right' });
    },
  });
};

export const useUploadAttendance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file) => uploadAttendance(file),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['monthlyAttendance'] });
      queryClient.invalidateQueries({ queryKey: ['employeeAttendanceHistory'] });
      queryClient.invalidateQueries({ queryKey: ['cycleAttendance'] });
      queryClient.invalidateQueries({ queryKey: ['employeeCycleAttendance'] });
      queryClient.invalidateQueries({ queryKey: ['adminLeaveRequests'] });
      const skipped = data?.error_count || 0;
      // The backend message now embeds the date range — keep toasts up a bit
      // longer so the date stays readable.
      if (skipped > 0) {
        toast.warn(data?.message || 'Some rows were skipped.', {
          autoClose: 6000, position: 'top-right',
        });
      } else {
        toast.success(data?.message || 'Attendance uploaded successfully.', {
          autoClose: 6000, position: 'top-right',
        });
      }
    },
    onError: (error) => {
      const backendMsg = error?.response?.data?.error
        || error?.response?.data?.detail
        || 'Could not upload the attendance file. Please try again.';
      toast.error(backendMsg, { autoClose: 5000, position: 'top-right' });
    },
  });
};

export const useDownloadAttendanceTemplate = () => {
  return useMutation({
    mutationFn: () => downloadAttendanceTemplate(),
    onSuccess: () => {
      toast.success('Template downloaded.', {
        autoClose: 3000, position: 'top-right',
      });
    },
    onError: () => {
      toast.error('Failed to download template.', {
        autoClose: 4000, position: 'top-right',
      });
    },
  });
};

export const useDownloadAttendanceExport = () => {
  return useMutation({
    mutationFn: (params) => downloadAttendanceExport(params),
    onSuccess: () => {
      toast.success('Attendance downloaded.', { autoClose: 3000, position: 'top-right' });
    },
    onError: () => {
      toast.error('Failed to download attendance.', { autoClose: 4000, position: 'top-right' });
    },
  });
};

export const useDownloadMonthlyAttendanceExport = () => {
  return useMutation({
    mutationFn: (params) => downloadMonthlyAttendanceExport(params),
    onSuccess: () => {
      toast.success('Monthly report downloaded.', { autoClose: 3000, position: 'top-right' });
    },
    onError: () => {
      toast.error('Failed to download monthly report.', { autoClose: 4000, position: 'top-right' });
    },
  });
};

export const useOverrideAttendance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ attendanceId, payload }) => overrideAttendance(attendanceId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monthlyAttendance'] });
      queryClient.invalidateQueries({ queryKey: ['employeeAttendanceHistory'] });
      queryClient.invalidateQueries({ queryKey: ['cycleAttendance'] });
      queryClient.invalidateQueries({ queryKey: ['employeeCycleAttendance'] });
      queryClient.invalidateQueries({ queryKey: ['adminLeaveRequests'] });
      toast.success('Attendance updated.', {
        autoClose: 3000, position: 'top-right',
      });
    },
    onError: (error) => {
      const backendMsg = error?.response?.data?.error
        || 'Failed to update attendance. Please try again.';
      toast.error(backendMsg, { autoClose: 4000, position: 'top-right' });
    },
  });
};
